import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Minus, Plus, X, ShoppingBag } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { cart, updateCart, user, setAuthOpen } = useApp();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!user) { setAuthOpen(true); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="max-w-[900px] mx-auto px-6 md:px-10 py-24 text-center">
        <ShoppingBag className="w-10 h-10 mx-auto text-neutral-400" />
        <h1 className="font-display font-black text-4xl tracking-tighter mt-6">Your bag is empty.</h1>
        <p className="text-neutral-500 mt-2">Every collection starts with a single obsession.</p>
        <Link
          to="/shop/all"
          className="inline-block mt-8 bg-black text-white px-6 py-3 font-mono-brand text-xs uppercase tracking-[0.2em] hover:bg-[#FF3B30] transition-colors"
          data-testid="empty-cart-shop-btn"
        >
          Browse the store →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
      <div className="font-mono-brand text-xs uppercase tracking-[0.3em] text-neutral-500">/ your bag</div>
      <h1 className="font-display font-black text-5xl md:text-6xl leading-none tracking-tighter mt-2">Checkout.</h1>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mt-12">
        <div className="md:col-span-8">
          <div className="border-t border-black/10">
            {cart.items.map((it) => (
              <div key={it.product_id} className="grid grid-cols-12 gap-4 py-6 border-b border-black/10 items-center" data-testid={`cart-item-${it.product_id}`}>
                <Link to={`/product/${it.product_id}`} className="col-span-3 md:col-span-2">
                  <div className="aspect-square overflow-hidden bg-neutral-100">
                    <img src={it.product.image + "?auto=format&fit=crop&w=300&q=80"} alt={it.product.name} className="w-full h-full object-cover" />
                  </div>
                </Link>
                <div className="col-span-9 md:col-span-5">
                  <Link to={`/product/${it.product_id}`} className="font-display text-lg leading-tight hover:text-[#FF3B30]">{it.product.name}</Link>
                  <div className="font-mono-brand text-xs text-neutral-500 uppercase mt-1">{it.product.category}</div>
                </div>
                <div className="col-span-6 md:col-span-3 flex items-center gap-2">
                  <button
                    onClick={() => updateCart(it.product_id, it.quantity - 1)}
                    className="w-8 h-8 border border-black/10 hover:border-black flex items-center justify-center"
                    data-testid={`qty-minus-${it.product_id}`}
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <span className="font-mono-brand text-sm w-6 text-center" data-testid={`qty-${it.product_id}`}>{it.quantity}</span>
                  <button
                    onClick={() => updateCart(it.product_id, it.quantity + 1)}
                    className="w-8 h-8 border border-black/10 hover:border-black flex items-center justify-center"
                    data-testid={`qty-plus-${it.product_id}`}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                </div>
                <div className="col-span-4 md:col-span-1 font-mono-brand text-sm text-right">
                  ${it.line_total.toFixed(2)}
                </div>
                <div className="col-span-2 md:col-span-1 flex justify-end">
                  <button
                    onClick={() => updateCart(it.product_id, 0)}
                    className="text-neutral-400 hover:text-[#FF3B30]"
                    data-testid={`remove-${it.product_id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="border border-black/10 p-8 sticky top-24">
            <div className="font-mono-brand text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">/ summary</div>
            <div className="flex items-baseline justify-between mb-2">
              <span className="text-neutral-500">Subtotal</span>
              <span className="font-mono-brand" data-testid="cart-subtotal">${cart.total.toFixed(2)}</span>
            </div>
            <div className="flex items-baseline justify-between mb-6 text-sm text-neutral-500">
              <span>Shipping</span>
              <span>Calculated at checkout</span>
            </div>
            <div className="flex items-baseline justify-between pt-4 border-t border-black/10">
              <span className="font-display text-lg">Total</span>
              <span className="font-display text-2xl font-black tracking-tight" data-testid="cart-total">${cart.total.toFixed(2)}</span>
            </div>
            <Button
              onClick={checkout}
              disabled={loading}
              className="w-full mt-6 rounded-none h-14 bg-black text-white hover:bg-[#FF3B30] font-mono-brand text-xs uppercase tracking-[0.2em]"
              data-testid="checkout-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Pay with Stripe →"}
            </Button>
            <p className="mt-3 font-mono-brand text-[10px] uppercase tracking-[0.2em] text-neutral-400 text-center">
              Test card: 4242 4242 4242 4242
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
